(() => {
  'use strict';

  const VERSION = 'NMRSTOR SEARCH RUNTIME LOADER V1';
  const KEY = '__NMRSTOR_SEARCH_RUNTIME_LOADER_V1__';

  if (window[KEY]) return;

  const bootScript = document.currentScript;
  const runtimeSrc =
    bootScript?.dataset?.nmrSearchRuntimeSrc || '';

  if (!runtimeSrc) return;

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;

  const state = {
    version: VERSION,
    status: 'idle',
    reason: '',
    startedAt: 0,
    readyAt: 0,
    failures: 0,
    hoverScheduled: 0,
    hoverCancelled: 0,
    intentLoads: 0,
    idleLoads: 0,
    idleSkipped: '',
    replayedInputs: 0
  };

  let runtimePromise = null;
  let runtimeNode = null;
  let hoverTimer = 0;
  let hoverOwner = null;
  let idleTimer = 0;
  let idleCallback = 0;

  const intentSelector = [
    '[data-nmr-layer-trigger="search"]',
    '[data-nmr-layer="search"]',
    '[data-nmr-main-search]',
    '[data-nmr-search-input]',
    '[data-nmr-main-search-form]'
  ].join(',');

  function isElement(value) {
    return value instanceof Element;
  }

  function intentOwner(target) {
    if (!isElement(target)) return null;
    return target.closest(intentSelector);
  }

  function constrainedIdle() {
    if (connection?.saveData) {
      state.idleSkipped = 'save-data';
      return true;
    }

    const type =
      String(connection?.effectiveType || '')
        .toLowerCase();

    if (
      type === '3g' ||
      /(^|-)2g$/.test(type)
    ) {
      state.idleSkipped =
        type || 'slow-network';
      return true;
    }

    return false;
  }

  function replayTypedInput() {
    window.queueMicrotask(() => {
      document
        .querySelectorAll(
          '[data-nmr-search-input], ' +
          '[data-nmr-main-search] input[type="search"]'
        )
        .forEach((input) => {
          if (
            !(input instanceof HTMLInputElement) ||
            !input.value.trim()
          ) {
            return;
          }

          state.replayedInputs++;

          input.dispatchEvent(
            new Event('input', {
              bubbles: true
            })
          );
        });
    });
  }

  function loadRuntime(
    reason = 'manual',
    priority = 'auto'
  ) {
    if (state.status === 'ready') {
      return Promise.resolve(true);
    }

    if (
      state.status === 'loading' &&
      runtimePromise
    ) {
      return runtimePromise;
    }

    state.status = 'loading';
    state.reason = reason;
    state.startedAt = Date.now();

    if (reason === 'idle') {
      state.idleLoads++;
    } else {
      state.intentLoads++;
    }

    runtimePromise =
      new Promise((resolve, reject) => {
        const script =
          document.createElement('script');

        runtimeNode = script;

        script.src = runtimeSrc;
        script.async = true;
        script.dataset.nmrSearchRuntime =
          'stage05';

        if (
          priority !== 'auto' &&
          'fetchPriority' in script
        ) {
          try {
            script.fetchPriority = priority;
          } catch (_) {}
        }

        script.addEventListener(
          'load',
          () => {
            state.status = 'ready';
            state.readyAt = Date.now();

            replayTypedInput();

            document.dispatchEvent(
              new CustomEvent(
                'nmr:search-runtime:ready',
                {
                  detail: {
                    reason,
                    startedAt:
                      state.startedAt,
                    readyAt:
                      state.readyAt
                  }
                }
              )
            );

            resolve(true);
          },
          { once: true }
        );

        script.addEventListener(
          'error',
          () => {
            state.status = 'error';
            state.failures++;
            runtimeNode = null;
            runtimePromise = null;
            reject(
              new Error(
                'Search runtime failed to load'
              )
            );
          },
          { once: true }
        );

        document.head.appendChild(script);
      });

    runtimePromise.catch(() => {});

    return runtimePromise;
  }

  function cancelHover() {
    if (!hoverTimer) return;

    window.clearTimeout(hoverTimer);
    hoverTimer = 0;
    hoverOwner = null;
    state.hoverCancelled++;
  }

  function scheduleHover(owner) {
    if (
      !owner ||
      state.status !== 'idle'
    ) {
      return;
    }

    cancelHover();

    hoverOwner = owner;
    state.hoverScheduled++;

    hoverTimer =
      window.setTimeout(() => {
        hoverTimer = 0;
        hoverOwner = null;

        loadRuntime(
          'hover-intent',
          'high'
        );
      }, 140);
  }

  document.addEventListener(
    'pointerover',
    (event) => {
      if (
        event.pointerType === 'touch'
      ) {
        return;
      }

      scheduleHover(
        intentOwner(event.target)
      );
    },
    {
      capture: true,
      passive: true
    }
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (!hoverOwner) return;

      const related =
        isElement(event.relatedTarget)
          ? event.relatedTarget
          : null;

      if (
        related &&
        hoverOwner.contains(related)
      ) {
        return;
      }

      cancelHover();
    },
    {
      capture: true,
      passive: true
    }
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!intentOwner(event.target)) {
        return;
      }

      cancelHover();

      loadRuntime(
        event.pointerType === 'touch'
          ? 'pointerdown-touch'
          : 'pointerdown',
        'high'
      );
    },
    {
      capture: true,
      passive: true
    }
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      if (!intentOwner(event.target)) {
        return;
      }

      loadRuntime(
        'touchstart',
        'high'
      );
    },
    {
      capture: true,
      passive: true
    }
  );

  document.addEventListener(
    'focusin',
    (event) => {
      if (!intentOwner(event.target)) {
        return;
      }

      cancelHover();

      loadRuntime(
        'focusin',
        'high'
      );
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!intentOwner(event.target)) {
        return;
      }

      loadRuntime(
        'click-fallback',
        'high'
      );
    },
    true
  );

  function runIdleWarm() {
    idleTimer = 0;

    if (
      state.status !== 'idle' ||
      document.visibilityState === 'hidden' ||
      constrainedIdle()
    ) {
      return;
    }

    if (
      typeof window.requestIdleCallback ===
        'function'
    ) {
      idleCallback =
        window.requestIdleCallback(
          () => {
            idleCallback = 0;

            if (
              state.status === 'idle' &&
              document.visibilityState !==
                'hidden'
            ) {
              loadRuntime(
                'idle',
                'low'
              );
            }
          },
          {
            timeout: 2500
          }
        );

      return;
    }

    idleTimer =
      window.setTimeout(() => {
        if (
          state.status === 'idle' &&
          document.visibilityState !==
            'hidden'
        ) {
          loadRuntime(
            'idle',
            'low'
          );
        }
      }, 900);
  }

  function scheduleIdleWarm() {
    if (
      state.status !== 'idle' ||
      constrainedIdle()
    ) {
      return;
    }

    idleTimer =
      window.setTimeout(
        runIdleWarm,
        5000
      );
  }

  function armIdleWarm() {
    if (document.prerendering) {
      document.addEventListener(
        'prerenderingchange',
        armIdleWarm,
        { once: true }
      );
      return;
    }

    if (
      document.readyState ===
        'complete'
    ) {
      scheduleIdleWarm();
      return;
    }

    window.addEventListener(
      'load',
      scheduleIdleWarm,
      {
        once: true,
        passive: true
      }
    );
  }

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
          'visible' &&
        state.status === 'idle' &&
        !idleTimer &&
        !idleCallback
      ) {
        scheduleIdleWarm();
      }
    },
    {
      passive: true
    }
  );

  window.addEventListener(
    'pageshow',
    (event) => {
      if (
        event.persisted &&
        state.status === 'idle'
      ) {
        scheduleIdleWarm();
      }
    },
    {
      passive: true
    }
  );

  window[KEY] = {
    load: loadRuntime,
    metrics() {
      return {
        ...state,
        runtimeSrc,
        runtimeConnected:
          Boolean(runtimeNode?.isConnected)
      };
    }
  };

  if (window.Shopify?.designMode) {
    loadRuntime(
      'design-mode',
      'low'
    );
  } else {
    armIdleWarm();
  }
})();